'use client'

import Link from '@/components/link-locale'
import { siteConfig } from '@/config/site'
import { ChannelTypeType } from '@/constants/channel'
import { useChannels } from '@/lib/api/hooks/use-channel'
import { GetUser } from '@/lib/api/hooks/use-user'
import { getAssetUrl } from '@/utils'
import { UserOutlined } from '@ant-design/icons'
import { Avatar, Button } from 'antd'
import useEmblaCarousel from 'embla-carousel-react'
import { ArrowDownCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'

import Loading from './loading'

export const CreatorAvatars = ({
  type,
  pageSize = siteConfig.pagination.pageSize,
}: {
  type: ChannelTypeType
  pageSize: number
}) => {
  const t = useTranslations()

  const { data: user } = GetUser()
  const [emblaRef] = useEmblaCarousel({ dragFree: true })
  const {
    data: channels,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChannels({
    params: {
      type,
      pageSize,
      // @ts-ignore
      userId: user?.id,
      categoryIds: user?.categoryIds || [],
      hasAvatar: true,
    },
  })

  const allChannels = useMemo(() => {
    return channels?.pages?.flatMap(page => page?.list ?? []) ?? []
  }, [channels?.pages])

  // console.log('🚀 ~ CreatorAvatars ~ channels:', channels)

  // if (!channels) return null

  const handleLoadMoreClick = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <Loading isLoading={isLoading}>
      <div className='embla hidden lg:block'>
        <div className='embla__viewport' ref={emblaRef}>
          <div className='embla__container flex-ro flex gap-x-[42.29px]'>
            {allChannels?.map(item => (
              <Link
                href={`/@${item.code}`}
                key={`${item.id}`}
                className='embla__slide flex cursor-pointer px-[6px]'
              >
                <div className='flex flex-col items-center gap-y-3'>
                  <Avatar
                    className='border-2 border-[#2B313D] shadow-lg hover:shadow-2xl'
                    shape='circle'
                    size={156}
                    alt='avatar'
                    icon={<UserOutlined />}
                    src={
                      item.avatar || getAssetUrl(siteConfig.placeholder.avatar)
                    }
                  />
                  <div className='flex flex-row'>
                    <span className='max-w-[120px] truncate text-sm font-bold leading-[14px] text-white'>
                      {item.name}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className='mx-auto grid grid-cols-2 gap-7 lg:hidden'>
        {allChannels?.map(item => (
          <Link
            href={`/@${item.code}`}
            key={`${item.id}`}
            className='flex cursor-pointer'
          >
            <div className='flex w-full flex-col items-center gap-y-3'>
              <Avatar
                className='border-2 border-[#2B313D] shadow-lg hover:shadow-2xl'
                shape='circle'
                size={148}
                alt='avatar'
                icon={<UserOutlined />}
                src={item.avatar || getAssetUrl(siteConfig.placeholder.avatar)}
              />
              <div className='flex flex-row'>
                <span className='max-w-[156px] truncate text-[13px] font-bold leading-[13px] text-white'>
                  {item.name}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {/* 暫時隱藏 */}
      {hasNextPage && (
        <Button
          className='mt-3 flex h-10 w-full gap-x-[10px] rounded border-none bg-[#1C2127] text-sm font-bold leading-[14px] text-[#808592] lg:hidden'
          onClick={handleLoadMoreClick}
          loading={isFetchingNextPage}
        >
          <ArrowDownCircle size={20} color='#808592' />
          {t('page.home.action.viewMore')}
        </Button>
      )}
    </Loading>
  )
}
